import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { LOGOS } from "../../lib/ablemp-data";
import { formatCpfCnpj } from "../../lib/masks";
import { isAdminRole } from "../../lib/roles";
import { api, formatApiErrorDetail } from "../api";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      const target =
        isAdminRole(user.role)
          ? "/admin/dashboard"
          : user.status === "ativo"
          ? "/portal/dashboard"
          : "/aguardando-aprovacao";
      navigate(location.state?.from?.pathname || target, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(identifier.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Login realizado com sucesso");
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  // If it looks like a document (starts with digit), apply CPF/CNPJ mask on the fly.
  const onIdentifierChange = (val) => {
    const trimmed = val.trim();
    if (trimmed && /^\d/.test(trimmed) && !trimmed.includes("@")) {
      setIdentifier(formatCpfCnpj(trimmed));
    } else {
      setIdentifier(val);
    }
  };

  const openForgot = () => {
    setForgotIdentifier(identifier.trim());
    setForgotMessage("");
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotIdentifier.trim()) return;
    setForgotBusy(true);
    setForgotMessage("");
    try {
      const r = await api.post("/auth/forgot-password", {
        identifier: forgotIdentifier.trim(),
      });
      setForgotMessage(
        r.data?.message ||
          "Se o cadastro existir, enviaremos as instruções por e-mail."
      );
    } catch (err) {
      setForgotMessage(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Falha ao enviar. Tente novamente."
      );
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0F14] text-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0F14] relative overflow-hidden">
        <div className="absolute inset-0 bg-grain-dark opacity-50 pointer-events-none" />
        <div className="relative">
          <Link to="/">
            <img src={LOGOS.white} alt="ABLEMP" className="h-16 w-auto" />
          </Link>
        </div>
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FEDF00]">
            Portal do Associado
          </div>
          <h1 className="mt-4 font-serif-display text-5xl leading-tight tracking-tight">
            Acesse a área restrita da{" "}
            <span className="italic font-light text-[#FEDF00]">ABLEMP</span>.
          </h1>
          <p className="mt-6 text-white/65 max-w-md leading-relaxed">
            Boletos, comunicados, fórum de associados, pautas e atas das
            reuniões em um único lugar.
          </p>
        </div>
        <div className="relative flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/45">
          <span className="h-1 w-6" style={{ background: "#002776" }} />
          <span className="h-1 w-6" style={{ background: "#009B3A" }} />
          <span className="h-1 w-6" style={{ background: "#FEDF00" }} />
          <span>Feito no Brasil</span>
        </div>
      </div>

      <div className="bg-white text-slate-900 flex items-center justify-center p-8">
        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="w-full max-w-md"
        >
          <Link to="/" className="lg:hidden inline-block mb-8">
            <img
              src={LOGOS.monoBlack}
              alt="ABLEMP"
              className="h-12 w-auto"
            />
          </Link>
          <h2 className="font-serif-display text-3xl">Entrar</h2>
          <p className="mt-2 text-sm text-slate-600">
            Acesse com seu <strong>CPF, CNPJ ou e-mail</strong> e senha
            cadastrados.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                CPF, CNPJ ou e-mail
              </label>
              <input
                data-testid="login-identifier"
                type="text"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => onIdentifierChange(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 focus:border-[#002776]"
                placeholder="000.000.000-00 · 00.000.000/0000-00 · seu@email.com.br"
                maxLength={80}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Senha
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 focus:border-[#002776]"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mt-4 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-sm"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-[#0A0F14] text-white py-3 text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#002776] transition-colors disabled:opacity-60"
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>

          <div className="mt-3 text-right">
            <button
              type="button"
              data-testid="login-forgot"
              onClick={openForgot}
              className="text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-[#002776]"
            >
              Esqueci minha senha
            </button>
          </div>

          <div className="mt-6 text-sm text-slate-600">
            Ainda não tem conta?{" "}
            <Link
              data-testid="login-link-signup"
              to="/cadastro"
              className="text-[#002776] font-semibold underline-offset-4 hover:underline"
            >
              Cadastre-se
            </Link>
          </div>
        </form>
      </div>

      {forgotOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !forgotBusy && setForgotOpen(false)}
        >
          <div
            className="bg-white text-slate-900 w-full max-w-md rounded-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="forgot-modal"
          >
            <form onSubmit={submitForgot}>
              <div className="p-5 border-b border-slate-200">
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                  Recuperar senha
                </div>
                <h3 className="text-lg font-semibold mt-1">
                  Enviar senha temporária
                </h3>
                <p className="text-sm text-slate-600 mt-2">
                  Informe seu <strong>CPF, CNPJ ou e-mail</strong>. Enviaremos uma
                  senha temporária para os e-mails cadastrados (contato principal
                  e representante legal).
                </p>
              </div>
              <div className="p-5">
                <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                  CPF, CNPJ ou e-mail
                </label>
                <input
                  data-testid="forgot-identifier"
                  autoFocus
                  value={forgotIdentifier}
                  onChange={(e) => setForgotIdentifier(e.target.value)}
                  className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
                  placeholder="000.000.000-00 · 00.000.000/0000-00 · seu@email.com.br"
                  disabled={forgotBusy || !!forgotMessage}
                  required
                />
                {forgotMessage && (
                  <div
                    data-testid="forgot-message"
                    className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm text-emerald-800"
                  >
                    {forgotMessage}
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  disabled={forgotBusy}
                  className="px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400 disabled:opacity-50"
                >
                  Fechar
                </button>
                {!forgotMessage && (
                  <button
                    type="submit"
                    data-testid="forgot-submit"
                    disabled={forgotBusy || !forgotIdentifier.trim()}
                    className="px-4 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-50"
                  >
                    {forgotBusy ? "Enviando..." : "Enviar senha"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
